import { HTTPError } from 'ky';

export class CaptchaFlowCancelled extends Error {}

/** Bridges the Aliyun widget callback to the existing server-side CAPTCHA check. */
const pendingResults = new Map<string, (verified: boolean) => void>();
const retries = new Map<string, Promise<string | undefined>>();

export function waitForCaptchaResult(token: string, nextToken?: Promise<string | undefined>) {
  // Dispose an older attempt if the SDK unexpectedly reuses its parameter.
  pendingResults.get(token)?.(false);
  const result = new Promise<boolean>((resolve) => {
    const finish = (verified: boolean) => {
      window.clearTimeout(timer);
      if (pendingResults.get(token) === finish) {
        pendingResults.delete(token);
        retries.delete(token);
      }
      resolve(verified);
    };
    const timer = window.setTimeout(() => {
      finish(false);
    }, 15_000);
    pendingResults.set(token, finish);
  });
  const finish = pendingResults.get(token);
  if (nextToken) {
    retries.set(token, nextToken);
  }
  return { result, cancel: () => finish?.(false) };
}

/** Only a successful response from the CAPTCHA-bearing endpoint confirms verification. */
export async function withCaptchaResult<T>(
  token: string | undefined,
  request: (captchaToken?: string) => Promise<T>
): Promise<T> {
  const finish = token ? pendingResults.get(token) : undefined;
  const nextToken = token ? retries.get(token) : undefined;
  try {
    const response = await request(token);
    finish?.(true);
    return response;
  } catch (error: unknown) {
    const body: unknown =
      error instanceof HTTPError && error.response.status === 422
        ? await error.response
            .clone()
            .json()
            .catch(() => null)
        : undefined;
    const captchaFailed =
      body && typeof body === 'object' && 'code' in body && body.code === 'session.captcha_failed';
    finish?.(false);
    // Only a rejected CAPTCHA is retried in the widget. Business/network failures still propagate.
    if (captchaFailed && nextToken) {
      const retryToken = await nextToken;
      if (!retryToken) {
        throw new CaptchaFlowCancelled();
      }
      return withCaptchaResult(retryToken, request);
    }
    throw error;
  }
}
